import type { FunctionComponent } from 'react';
import { paletteHelper, spacingHelper } from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { SchoolFactCard } from './subcomponents/SchoolFactCard/SchoolFactCard';
import { ContentBrick } from '../../../../../app-kit/components/ContentBrick/ContentBrick';
import { ExternalLink } from '../../../../../app-kit/components/ExternalLink/ExternalLink';
import { WebLinkProps } from '../../../../../app-kit/components/WebLink/WebLink';
import { DisclaimerIcon } from '../../../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { useModalContext } from '../../../../../contexts/ModalContext/ModalContext';
import { trackDisclaimerModalOpen } from '../../../../../analytics/event-tracking/trackDisclaimerModalOpen';

export interface SchoolFact {
  title: string;
  subtitle: string;
}

interface SchoolFactsBrickProps {
  title: string;
  subtitle: string;
  facts: SchoolFact[];
  naplanResultsLink: WebLinkProps;
}

const FactsWrapper = styled.section`
  display: flex;
  justify-content: space-evenly;
  gap: ${spacingHelper('extraSmall')};
  flex-wrap: wrap;
`;

const LinkWrapper = styled.section`
  display: flex;
  justify-content: center;
`;

const StyledExternalLink = styled(ExternalLink)`
  color: ${paletteHelper('linkPrimary')};
  margin-top: calc(${spacingHelper('medium')} + ${spacingHelper('twoExtraSmall')});
  margin-bottom: ${spacingHelper('extraSmall')};
  /* override rules so that button color doesn't change on hover */
  &:hover,
  &:focus,
  &:focus-within {
    background-color: initial;
    color: ${paletteHelper('linkHoveredPrimary')};
  }
`;

export const SchoolFactsBrick: FunctionComponent<SchoolFactsBrickProps> = ({
  title,
  subtitle,
  facts,
  naplanResultsLink: { title: naplanLinkTitle, url, variant, iconInfo, rel },
}) => {
  const { displayModal } = useModalContext();

  return (
    <ContentBrick
      title={title}
      subtitle={subtitle}
      headerChildren={
        <DisclaimerIcon
          title="Open School Facts Disclaimer"
          onClick={() => {
            displayModal({ name: 'schoolFactsDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'school_page__school_facts_brick___disclaimer_link',
            });
          }}
        />
      }
    >
      <FactsWrapper>
        {facts.map((fact) => (
          <SchoolFactCard key={fact.title} title={fact.title} subtitle={fact.subtitle} />
        ))}
      </FactsWrapper>
      <LinkWrapper>
        <StyledExternalLink
          href={url}
          linkVariant={variant}
          iconInfo={iconInfo}
          outboundLinkType="naplan-outbound-link"
          rel={rel || undefined}
        >
          {naplanLinkTitle}
        </StyledExternalLink>
      </LinkWrapper>
    </ContentBrick>
  );
};
